// Convert a list of applied search-filter chips into a highlight set.
//
// Semantics:
//  - chips of the SAME kind are OR-combined (e.g. two customers = chains for
//    either customer)
//  - chips of DIFFERENT kinds are AND-combined (e.g. customer X + raw Y =
//    only chains for X that consume Y)
//
// This is the standard faceted-filter pattern (same as Amazon / Slack).
//
// The output is (a) the matching row-index set, and (b) the matching
// sankey-node-id set — derived from the rows' raws/bulk/container/order
// columns. The chart's existing dim logic accepts a `filterHighlightNodeIds`
// prop to drive opacity.

import { STAGES } from './mppHelpers';
import type { EntityKind, SearchEntity } from './searchIndex';
import type { MPPRow } from './types';

export interface FilterResult {
  active: boolean; // false when there are no chips applied
  rowIndices: Set<number>;
  nodeIds: Set<string>;
}

export function applyFilters(
  chips: readonly SearchEntity[],
  rows: readonly MPPRow[],
): FilterResult {
  if (chips.length === 0) {
    return { active: false, rowIndices: new Set(), nodeIds: new Set() };
  }
  const byKind = new Map<EntityKind, SearchEntity[]>();
  for (const c of chips) {
    const arr = byKind.get(c.kind) ?? [];
    arr.push(c);
    byKind.set(c.kind, arr);
  }
  let working: Set<number> | null = null;
  for (const group of byKind.values()) {
    const groupRows = new Set<number>();
    for (const chip of group) {
      for (const idx of chip.rowIndices) groupRows.add(idx);
    }
    if (working === null) {
      working = groupRows;
    } else {
      // intersect across kinds
      const next = new Set<number>();
      for (const i of working) if (groupRows.has(i)) next.add(i);
      working = next;
    }
  }
  const rowIndices = working ?? new Set<number>();
  const nodeIds = new Set<string>();
  for (const idx of rowIndices) {
    const r = rows[idx];
    if (!r) continue;
    for (const s of STAGES) {
      const v = r[s];
      if (v != null && String(v).trim() !== '') {
        nodeIds.add(`${s}::${String(v).trim()}`);
      }
    }
  }
  return { active: true, rowIndices, nodeIds };
}
